import React, { useState, useEffect } from 'react';
import { getSenjaTestimonialsWithFallback } from '../../utils/senjaApi';
import styles from './HorizontalTestimonials.module.scss';

const HorizontalTestimonials = ({
  senjaApiKey = null,
  speed = 30,
  limit = 1000, // Increased default to 1000
  minRating = 4,
  enableLengthFiltering = true, // Enable filtering by default
  minLength = 200, // Minimum characters - changed from 300 to 200
  maxLength = 300, // Maximum characters - changed from 500 to 300
  hideHeader = false, // Hide the "Testimonials" header
}) => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTestimonials = async () => {
      setLoading(true);
      setError(null);

      try {
        console.log(
          `🚀 Fetching ${limit} testimonials with ${minLength}-${maxLength} character filter...`,
        );

        const fetchedTestimonials = await getSenjaTestimonialsWithFallback(
          senjaApiKey,
          {
            totalLimit: limit, // Total to fetch before filtering
            minRating,
            type: 'text', // Only text testimonials for better display
            enableLengthFiltering,
            minLength,
            maxLength,
          },
        );

        if (fetchedTestimonials && fetchedTestimonials.length > 0) {
          setTestimonials(fetchedTestimonials);
          console.log(
            `✅ Successfully loaded ${fetchedTestimonials.length} filtered testimonials`,
          );
        } else {
          throw new Error('No testimonials received');
        }
      } catch (err) {
        console.error('Failed to load testimonials:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchTestimonials();
  }, [
    senjaApiKey,
    limit,
    minRating,
    enableLengthFiltering,
    minLength,
    maxLength,
  ]);

  const createDuplicatedArray = (arr, minLength = 40) => {
    if (arr.length === 0) return [];

    const result = [...arr];
    while (result.length < minLength) {
      result.push(...arr);
    }
    return result;
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, index) => (
      <span
        key={index}
        className={`${styles.star} ${index < rating ? styles.filled : ''}`}
      >
        ★
      </span>
    ));
  };

  const renderTestimonialCard = (testimonial, index) => (
    <div
      key={`${testimonial.id}-${index}`}
      className={styles.testimonial_wrapper}
    >
      <div className={styles.testimonial_card}>
        <div className={styles.card_header}>
          {/* Handle both image URLs and initial avatar objects */}
          {testimonial.avatar &&
          typeof testimonial.avatar === 'object' &&
          testimonial.avatar.type === 'initial' ? (
            <div
              className={styles.initial_avatar}
              style={{
                backgroundColor: testimonial.avatar.backgroundColor,
                color: testimonial.avatar.textColor,
              }}
            >
              {testimonial.avatar.initial}
            </div>
          ) : (
            <img
              src={testimonial.avatar}
              alt={testimonial.name}
              className={styles.avatar}
              onError={(e) => {
                // Create an initial avatar as fallback
                const initial =
                  testimonial.name && testimonial.name.trim()
                    ? testimonial.name.trim()[0].toUpperCase()
                    : '?';
                const colors = [
                  '#FFE4E1',
                  '#E1F5FE',
                  '#F3E5F5',
                  '#E8F5E8',
                  '#FFF3E0',
                  '#E0E7FF',
                ];
                const hash = testimonial.name
                  ? testimonial.name
                      .split('')
                      .reduce((acc, char) => acc + char.charCodeAt(0), 0)
                  : 0;
                const backgroundColor = colors[hash % colors.length];

                // Replace the img with a div
                const parent = e.target.parentNode;
                const initialDiv = document.createElement('div');
                initialDiv.className = styles.initial_avatar;
                initialDiv.style.backgroundColor = backgroundColor;
                initialDiv.style.color = '#374151';
                initialDiv.textContent = initial;
                parent.replaceChild(initialDiv, e.target);
              }}
            />
          )}
          <div className={styles.user_info}>
            <h4 className={styles.name}>{testimonial.name}</h4>
            <p className={styles.role}>{testimonial.role}</p>
          </div>
        </div>

        <div className={styles.rating}>{renderStars(testimonial.rating)}</div>

        <div className={styles.content}>
          <h5 className={styles.testimonial_title}>{testimonial.title}</h5>
          <p className={styles.testimonial_text}>{testimonial.content}</p>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className={styles.testimonials_section}>
        {!hideHeader && (
          <div className={styles.testimonials_header}>
            <h2 className={styles.testimonials_title}>Testimonials</h2>
            <p className={styles.testimonials_subtitle}>
              See what our customers are saying
            </p>
          </div>
        )}
        <div className={styles.loading_container}>
          <div className={styles.loading_spinner}></div>
          <p>Loading testimonials...</p>
        </div>
      </div>
    );
  }

  if (error && testimonials.length === 0) {
    return (
      <div className={styles.testimonials_section}>
        {!hideHeader && (
          <div className={styles.testimonials_header}>
            <h2 className={styles.testimonials_title}>Testimonials</h2>
            <p className={styles.testimonials_subtitle}>
              See what our customers are saying
            </p>
          </div>
        )}
        <div className={styles.error_container}>
          <p>Unable to load testimonials</p>
          <small>Using demo testimonials</small>
        </div>
      </div>
    );
  }

  const row1Testimonials = createDuplicatedArray(
    testimonials.slice(0, Math.ceil(testimonials.length / 2)),
  );
  const row2Testimonials = createDuplicatedArray(
    testimonials.slice(Math.ceil(testimonials.length / 2)),
  );

  return (
    <div className={styles.testimonials_section}>
      {!hideHeader && (
        <div className={styles.testimonials_header}>
          <h2 className={styles.testimonials_title}>Testimonials</h2>
          <p className={styles.testimonials_subtitle}>
            See what our customers are saying
          </p>
        </div>
      )}

      <div className={styles.testimonials_container}>
        {/* Row 1 - Moving right */}
        <div className={`${styles.testimonials_row} ${styles.move_right}`}>
          <div
            className={styles.testimonials_track}
            style={{
              '--animation-duration': `${speed}s`,
            }}
          >
            {row1Testimonials.map((testimonial, index) =>
              renderTestimonialCard(testimonial, index),
            )}
          </div>
        </div>

        {/* Row 2 - Moving left with offset */}
        <div className={`${styles.testimonials_row} ${styles.move_left}`}>
          <div
            className={styles.testimonials_track}
            style={{
              '--animation-duration': `${speed + 6}s`,
              '--animation-delay': `-${speed / 4}s`,
            }}
          >
            {row2Testimonials.map((testimonial, index) =>
              renderTestimonialCard(testimonial, index),
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HorizontalTestimonials;
